import { openDB, type IDBPDatabase, type DBSchema } from 'idb';
import { supabase } from './supabase';

/**
 * Write outbox — Layer 3 of offline support.
 *
 * When the user creates a record (sign-in, daily check, accident report)
 * while offline, instead of failing we queue the operation here. On
 * reconnect the sync engine drains the queue in submission order.
 *
 * Key design choices:
 *   - Stored in IndexedDB so it survives app restarts.
 *   - Each entry is a self-contained {kind, payload} blob — the sync engine
 *     dispatches by `kind` to the right Supabase insert.
 *   - Entries get a tempId (uuid) the client uses immediately, so the UI
 *     can reference "this sign-in I just created" before the server has a
 *     real id. On successful replay we record the server id in the entry.
 *   - Failed replays retain the entry with an error message. The user can
 *     retry from Settings → Sign-in queue (built in a follow-up).
 *
 * NOT covered in this pass (Layer 4 candidates):
 *   - Photo uploads (need separate strategy — base64 in IDB or blob store)
 *   - Conflict resolution for edits (last-write-wins on form fields)
 *   - Optimistic UI integration in the page components
 */

// ============================================================
// Schema — separate DB from the read cache so they can evolve independently
// ============================================================

export type OutboxKind =
  | 'sign_in.create'
  | 'sign_in.sign_out'         // setting time_out + signature on an existing sign-in
  | 'tick_run.create'
  | 'form.tbt'
  | 'form.accident'
  | 'form.subbie_induction';

export type OutboxStatus = 'pending' | 'in_flight' | 'done' | 'error';

export interface OutboxEntry {
  id:           string;                   // tempId, client-generated uuid
  orgId:        string;                   // for filtering / cleanup on sign-out
  kind:         OutboxKind;
  payload:      Record<string, unknown>;  // kind-specific shape
  createdAt:    number;                   // ms epoch
  status:       OutboxStatus;
  attempts:     number;
  lastErrorAt:  number | null;
  lastError:    string | null;
  serverId:     string | null;            // populated on successful replay
}

interface OutboxSchema extends DBSchema {
  outbox: {
    key:     string;
    value:   OutboxEntry;
    indexes: {
      'by-org':       string;
      'by-status':    string;
      'by-created':   number;
    };
  };
}

const DB_NAME    = 'safe_and_sorted_outbox';
const DB_VERSION = 1;

let _dbPromise: Promise<IDBPDatabase<OutboxSchema>> | null = null;

function db(): Promise<IDBPDatabase<OutboxSchema>> {
  if (_dbPromise) return _dbPromise;
  _dbPromise = openDB<OutboxSchema>(DB_NAME, DB_VERSION, {
    upgrade(database) {
      if (!database.objectStoreNames.contains('outbox')) {
        const s = database.createObjectStore('outbox', { keyPath: 'id' });
        s.createIndex('by-org',     'orgId',     { unique: false });
        s.createIndex('by-status',  'status',    { unique: false });
        s.createIndex('by-created', 'createdAt', { unique: false });
      }
    },
  });
  return _dbPromise;
}

// ============================================================
// Public API — enqueue / list / drain
// ============================================================

/** Enqueue a write. Returns the tempId so the UI can reference it. */
export async function enqueue(
  kind: OutboxKind,
  orgId: string,
  payload: Record<string, unknown>,
): Promise<string> {
  const entry: OutboxEntry = {
    id:           crypto.randomUUID(),
    orgId,
    kind,
    payload,
    createdAt:    Date.now(),
    status:       'pending',
    attempts:     0,
    lastErrorAt:  null,
    lastError:    null,
    serverId:     null,
  };
  const d = await db();
  await d.put('outbox', entry);
  // Kick the sync engine if we're online — drains immediately rather than
  // waiting for the next online event.
  if (typeof navigator !== 'undefined' && navigator.onLine) {
    void drainOutbox();
  }
  return entry.id;
}

/** All entries for an org, newest first. Used by the queue UI. */
export async function listOutbox(orgId: string): Promise<OutboxEntry[]> {
  const d = await db();
  const rows = await d.getAllFromIndex('outbox', 'by-org', orgId);
  return rows.sort((a, b) => b.createdAt - a.createdAt);
}

/** Count of pending + erroring entries — surfaced as a badge in the UI. */
export async function pendingCount(): Promise<number> {
  const d = await db();
  const rows = await d.getAllFromIndex('outbox', 'by-status', 'pending');
  const errs = await d.getAllFromIndex('outbox', 'by-status', 'error');
  return rows.length + errs.length;
}

/** Manually retry a failed entry. Sets status back to pending + kicks drain. */
export async function retryEntry(id: string): Promise<void> {
  const d = await db();
  const e = await d.get('outbox', id);
  if (!e) return;
  e.status = 'pending';
  e.lastError = null;
  e.lastErrorAt = null;
  await d.put('outbox', e);
  if (typeof navigator !== 'undefined' && navigator.onLine) {
    void drainOutbox();
  }
}

/** Discard an entry — user gives up on a stuck record. */
export async function discardEntry(id: string): Promise<void> {
  const d = await db();
  await d.delete('outbox', id);
}

/** Clear all completed entries — keeps the queue tidy. Called periodically. */
export async function pruneDone(): Promise<void> {
  const d = await db();
  const rows = await d.getAllFromIndex('outbox', 'by-status', 'done');
  // Keep last 24 hours of "done" rows for audit, drop the older ones.
  const cutoff = Date.now() - 24 * 60 * 60 * 1000;
  const tx = d.transaction('outbox', 'readwrite');
  await Promise.all(
    rows.filter((r) => r.createdAt < cutoff).map((r) => tx.store.delete(r.id)),
  );
  await tx.done;
}

// ============================================================
// Media helpers — base64 ⇄ Blob for offline payloads
// ============================================================
// Photos and signatures captured offline are stored as base64 data URLs in
// the outbox payload (IndexedDB can hold these comfortably for our scale —
// 5–10 MB per record is fine, blowing past that is a v2 problem). On replay
// we decode back to Blob and upload to Supabase Storage.

/** Convert a Blob to a base64 data URL. Used by form pages before enqueue. */
export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(blob);
  });
}

/** Convert a base64 data URL back to a Blob. Used at dispatch time. */
function dataUrlToBlob(dataUrl: string): Blob {
  const comma = dataUrl.indexOf(',');
  const meta = dataUrl.slice(5, comma); // e.g. "image/png;base64"
  const isBase64 = meta.endsWith(';base64');
  const mime = isBase64 ? meta.slice(0, -';base64'.length) : meta;
  const data = dataUrl.slice(comma + 1);
  if (isBase64) {
    const bin = atob(data);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime || 'application/octet-stream' });
  }
  return new Blob([decodeURIComponent(data)], { type: mime || 'text/plain' });
}

/** Upload a base64 signature data URL to the signatures bucket and return path. */
async function uploadSigDataUrl(orgId: string, formId: string, suffix: string, dataUrl: string): Promise<string> {
  const blob = dataUrlToBlob(dataUrl);
  const path = `${orgId}/form-${formId}/${suffix}.png`;
  const { error } = await supabase.storage
    .from('signatures')
    .upload(path, blob, { contentType: 'image/png', upsert: true });
  if (error) throw error;
  return path;
}

/** Upload an array of base64 photo data URLs to a bucket; returns paths in order. */
async function uploadPhotoDataUrls(bucket: string, orgId: string, formId: string, dataUrls: string[]): Promise<string[]> {
  const paths: string[] = [];
  for (let i = 0; i < dataUrls.length; i++) {
    const blob = dataUrlToBlob(dataUrls[i]);
    const path = `${orgId}/form-${formId}/${i}.jpg`;
    const { error } = await supabase.storage
      .from(bucket)
      .upload(path, blob, { contentType: blob.type || 'image/jpeg', upsert: true });
    if (error) throw error;
    paths.push(path);
  }
  return paths;
}

// ============================================================
// Dispatch — per-kind replay handlers
// ============================================================

async function dispatch(entry: OutboxEntry): Promise<{ serverId: string }> {
  switch (entry.kind) {
    case 'sign_in.create': {
      const p = entry.payload as {
        orgId: string;
        siteId: string;
        full_name: string;
        company: string;
        trade_role: string | null;
        mobile: string | null;
        vehicle_reg: string | null;
        override_reason?: string | null;
        in_lat: number | null;
        in_lng: number | null;
        in_address: string | null;
        in_accuracy_m: number | null;
        confirmations_accepted: boolean[];
        is_first_aider?: boolean;
        requires_induction?: boolean;
        sign_in_type?: 'worker' | 'visitor';
        visitor_host?: string | null;
        time_in?: string;
      };
      const { data, error } = await supabase
        .from('sign_ins')
        .insert({
          org_id: p.orgId,
          site_id: p.siteId,
          full_name: p.full_name,
          company: p.company,
          trade_role: p.trade_role,
          mobile: p.mobile,
          vehicle_reg: p.vehicle_reg,
          override_reason: p.override_reason ?? null,
          in_lat: p.in_lat,
          in_lng: p.in_lng,
          in_address: p.in_address,
          in_accuracy_m: p.in_accuracy_m,
          confirmations_accepted: p.confirmations_accepted,
          is_first_aider: p.is_first_aider ?? false,
          requires_induction: p.requires_induction ?? false,
          sign_in_type: p.sign_in_type ?? 'worker',
          visitor_host: p.visitor_host ?? null,
          // Preserve original sign-in time even though we're inserting now
          time_in: p.time_in ?? new Date(entry.createdAt).toISOString(),
        })
        .select('id')
        .single();
      if (error) throw error;
      return { serverId: (data as { id: string }).id };
    }

    case 'sign_in.sign_out': {
      const p = entry.payload as {
        signInId: string;
        time_out: string;
        signature_out_url?: string | null;
        out_lat: number | null;
        out_lng: number | null;
        out_address: string | null;
        out_accuracy_m: number | null;
      };
      const { error } = await supabase
        .from('sign_ins')
        .update({
          time_out: p.time_out,
          signature_out_url: p.signature_out_url ?? null,
          out_lat: p.out_lat,
          out_lng: p.out_lng,
          out_address: p.out_address,
          out_accuracy_m: p.out_accuracy_m,
        })
        .eq('id', p.signInId);
      if (error) throw error;
      return { serverId: p.signInId };
    }

    case 'tick_run.create': {
      // Tick list run — single insert into tick_list_runs. Failed items can
      // include photos which were captured offline as base64 data URLs.
      const p = entry.payload as {
        runId: string;
        orgId: string;
        siteId: string;
        kind: 'daily' | 'weekly' | 'monthly' | 'telehandler_weekly';
        performed_by_name: string;
        responses: Record<string, unknown>;
        notes: string | null;
        // photo_data_urls: { [responseKey: string]: string[] } — base64 per item
        photo_data_urls?: Record<string, string[]>;
      };

      // Upload photos first so the responses payload can carry their paths.
      // tick-run photos historically went to the 'accident-photos' bucket
      // (mistake — but preserved for compatibility with existing reads).
      // We mirror that here so signed-url generation keeps working.
      const responses = { ...(p.responses as Record<string, Record<string, unknown>>) };
      if (p.photo_data_urls) {
        for (const [key, urls] of Object.entries(p.photo_data_urls)) {
          if (!urls.length) continue;
          const paths: string[] = [];
          for (let i = 0; i < urls.length; i++) {
            const blob = dataUrlToBlob(urls[i]);
            const path = `${p.orgId}/run-${p.runId}/${key}-${i}.jpg`;
            const { error } = await supabase.storage
              .from('accident-photos')
              .upload(path, blob, { contentType: blob.type || 'image/jpeg', upsert: true });
            if (error) throw error;
            paths.push(path);
          }
          const existing = responses[key] ?? {};
          responses[key] = { ...existing, photo_paths: paths };
        }
      }

      const { data, error } = await supabase
        .from('tick_list_runs')
        .insert({
          id: p.runId,
          org_id: p.orgId,
          site_id: p.siteId,
          kind: p.kind,
          performed_by_name: p.performed_by_name,
          responses,
          notes: p.notes,
        })
        .select('id')
        .single();
      if (error) throw error;
      return { serverId: (data as { id: string }).id };
    }

    case 'form.accident':
    case 'form.subbie_induction':
    case 'form.tbt': {
      // The three form kinds share the same database table (`forms`) and the
      // same media shape (one reporter signature + N photos). TBT additionally
      // has attendees, each with their own signature — handled below.
      // Subbie induction also has cascading inserts into `subcontractors` and
      // `subcontractor_inductions` — handled at the end of this case.
      const p = entry.payload as {
        formId: string;
        orgId: string;
        siteId: string;
        kind: 'toolbox_talk' | 'accident' | 'subcontractor_induction';
        title: string;
        performed_by_name: string;
        data: Record<string, unknown>;
        is_riddor?: boolean;
        riddor_flags?: Record<string, boolean> | null;
        attendee_count?: number;
        // Offline-only fields:
        signature_data_url?: string | null;
        photo_data_urls?: string[];
        photo_bucket?: 'accident-photos' | 'subbie-induction-photos';
        attendees?: Array<{
          full_name: string;
          company: string | null;
          verbal_only: boolean;
          signature_data_url?: string | null;
        }>;
        // Subbie-induction cascade fields:
        subbie_cascade?: {
          company_name: string;
          trade: string | null;
          main_contact: string | null;
          mobile: string | null;
          address: string | null;
          pl_insurance_expiry: string | null;
          el_insurance_expiry: string | null;
          approved_by: string | null;
          first_aider?: boolean;
          first_aid_cert_expiry?: string | null;
          person_name: string;
        };
      };

      // Upload photos first so their paths can go into the form's data blob.
      let formData: Record<string, unknown> = { ...p.data };
      if (p.photo_data_urls && p.photo_data_urls.length > 0 && p.photo_bucket) {
        const photoPaths = await uploadPhotoDataUrls(p.photo_bucket, p.orgId, p.formId, p.photo_data_urls);
        formData = { ...formData, photo_paths: photoPaths };
      }

      // Insert the form row
      const { data, error } = await supabase
        .from('forms')
        .insert({
          id: p.formId,
          org_id: p.orgId,
          site_id: p.siteId,
          kind: p.kind,
          title: p.title,
          performed_by_name: p.performed_by_name,
          data: formData,
          is_riddor: p.is_riddor ?? false,
          riddor_flags: p.riddor_flags ?? null,
          attendee_count: p.attendee_count ?? null,
        })
        .select('id')
        .single();
      if (error) throw error;

      // Upload reporter signature (if captured) and attach to form
      if (p.signature_data_url) {
        const sigPath = await uploadSigDataUrl(p.orgId, p.formId, 'in', p.signature_data_url);
        const { error: sigErr } = await supabase
          .from('forms')
          .update({ signature_url: sigPath })
          .eq('id', p.formId);
        if (sigErr) throw sigErr;
      }

      // TBT attendees — each gets a row + optional signature
      if (p.attendees && p.attendees.length > 0) {
        for (let idx = 0; idx < p.attendees.length; idx++) {
          const a = p.attendees[idx];
          let attendeeSigPath: string | null = null;
          if (!a.verbal_only && a.signature_data_url) {
            attendeeSigPath = await uploadSigDataUrl(p.orgId, p.formId, `attendee-${idx}`, a.signature_data_url);
          }
          const { error: attErr } = await supabase
            .from('form_attendees')
            .insert({
              form_id: p.formId,
              full_name: a.full_name,
              company: a.company,
              signature_url: attendeeSigPath,
              verbal_only: a.verbal_only,
            });
          if (attErr) throw attErr;
        }
      }

      // Subbie induction cascade — create/find the subcontractor and link an
      // induction row to it. Mirrors the online path in FormSubbie.tsx so
      // syncing an offline induction produces the same DB state as filing it
      // online would have.
      if (p.subbie_cascade) {
        const sc = p.subbie_cascade;
        // Find existing subcontractor by company name within the org
        const { data: existing, error: findErr } = await supabase
          .from('subcontractors')
          .select('id, pl_insurance_expiry, el_insurance_expiry, trade, mobile, address, first_aider, first_aid_cert_expiry')
          .eq('org_id', p.orgId)
          .ilike('company_name', sc.company_name)
          .is('archived_at', null)
          .limit(1);
        if (findErr) throw findErr;
        let subcontractorId: string;
        if (existing && existing.length > 0) {
          const ex = existing[0] as { id: string };
          subcontractorId = ex.id;
          // (Patch logic skipped here for brevity — re-induction within the
          // sync window is an edge case; the next online induction will pick
          // up any new insurance dates.)
        } else {
          const { data: created, error: createErr } = await supabase
            .from('subcontractors')
            .insert({
              org_id: p.orgId,
              company_name: sc.company_name,
              trade: sc.trade,
              main_contact: sc.main_contact,
              mobile: sc.mobile,
              address: sc.address,
              pl_insurance_expiry: sc.pl_insurance_expiry,
              el_insurance_expiry: sc.el_insurance_expiry,
              approved_by: sc.approved_by,
              approved_at: new Date().toISOString(),
              first_aider: sc.first_aider ?? false,
              first_aid_cert_expiry: sc.first_aid_cert_expiry ?? null,
            })
            .select('id')
            .single();
          if (createErr) throw createErr;
          subcontractorId = (created as { id: string }).id;
        }
        // Link an induction row
        const { error: indErr } = await supabase
          .from('subcontractor_inductions')
          .insert({
            org_id: p.orgId,
            subcontractor_id: subcontractorId,
            site_id: p.siteId,
            person_name: sc.person_name,
            form_id: p.formId,
          });
        if (indErr) throw indErr;
      }

      return { serverId: (data as { id: string }).id };
    }

    default: {
      // Defensive — TypeScript should catch unhandled kinds at compile time
      // via the exhaustive OutboxKind union, but if a future kind is added
      // without a case, fail loudly rather than silently.
      const exhaustive: never = entry.kind;
      throw new Error(`Unhandled outbox kind: ${String(exhaustive)}`);
    }
  }
}

// ============================================================
// Sync engine — drains the queue
// ============================================================

let _draining = false;

/** Drains pending entries in submission order. Idempotent — safe to call repeatedly. */
export async function drainOutbox(): Promise<void> {
  if (_draining) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  _draining = true;
  try {
    const d = await db();
    let entries = await d.getAllFromIndex('outbox', 'by-status', 'pending');
    // Process in submission order so a sign-in goes in before its sign-out
    entries = entries.sort((a, b) => a.createdAt - b.createdAt);

    for (const entry of entries) {
      // Mark in-flight so concurrent drains don't double-process
      entry.status = 'in_flight';
      entry.attempts += 1;
      await d.put('outbox', entry);

      try {
        const { serverId } = await dispatch(entry);
        entry.status = 'done';
        entry.serverId = serverId;
        entry.lastError = null;
        entry.lastErrorAt = null;
        await d.put('outbox', entry);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        entry.status = 'error';
        entry.lastError = msg;
        entry.lastErrorAt = Date.now();
        await d.put('outbox', entry);
        console.warn('[outbox] replay failed:', entry.kind, msg);
        // Don't stop the whole drain — keep processing other entries.
      }
    }
  } catch (e) {
    // iOS Safari aggressively closes IndexedDB connections when the app
    // backgrounds. If that happens between us getting the db handle and
    // starting a transaction, idb throws InvalidStateError ("The database
    // connection is closing"). The pending entries are still durable —
    // the next focus / online / 60s tick will reopen the DB and retry.
    // Swallowing this prevents the spurious Sentry alert; any OTHER
    // error still gets logged for debugging.
    const name = (e as { name?: string })?.name;
    if (name === 'InvalidStateError' || name === 'AbortError') {
      // expected during backgrounding; next drain will pick up where we left off
      return;
    }
    console.warn('[outbox] drain aborted:', e);
  } finally {
    _draining = false;
  }
}

// ============================================================
// Auto-drain — wire up event listeners once
// ============================================================

let _initialised = false;
export function initOutboxAutoSync(): void {
  if (_initialised) return;
  _initialised = true;

  // On reconnect
  window.addEventListener('online', () => { void drainOutbox(); });

  // On tab focus (the offline → online event sometimes doesn't fire on
  // mobile Safari when waking from sleep; focus is a reliable backup)
  window.addEventListener('focus', () => { void drainOutbox(); });

  // Periodic retry every 60s while pending entries exist. Cheap.
  setInterval(() => {
    if (typeof navigator !== 'undefined' && navigator.onLine) {
      void drainOutbox();
    }
  }, 60_000);

  // Try once on init — covers the case where the page was loaded fresh
  // online and there are stale pending entries from a previous session.
  if (typeof navigator !== 'undefined' && navigator.onLine) {
    void drainOutbox();
  }
}
